"use client"

import {
  Briefcase,
  FlaskConical,
  GraduationCap,
  LucideProps,
  Search
} from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import { TrackedButton } from "@/components/analytics"

interface Category {
  count: number
  id: string
  name: string
}

interface SubjectSearchInputProps {
  categories: Category[]
  onCategoryChange?: (categoryId: string) => void
  onSearchChange?: (value: string) => void
}

const categoryIcons: Record<string, React.ElementType<LucideProps>> = {
  Academic: GraduationCap,
  Professional: Briefcase,
  Specialized: FlaskConical
}

export default function SubjectSearchInput({
  categories,
  onCategoryChange,
  onSearchChange
}: SubjectSearchInputProps) {
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [searchTerm, setSearchTerm] = useState("")
  const [showNoResults, setShowNoResults] = useState(false)
  const [computedCounts, setComputedCounts] = useState<Record<string, number>>(
    () =>
      Object.fromEntries(categories.map((c) => [c.id, c.count])) as Record<
        string,
        number
      >
  )

  // Cache DOM references and metadata once
  const subjectCardsRef = useRef<HTMLElement[]>([])
  const subjectMetaRef = useRef<{ name: string; tier: null | string }[]>([])
  const tierGroupsRef = useRef<HTMLElement[]>([])

  useEffect(() => {
    const container = document.getElementById("subjects-container")
    if (!container) return

    const cards = Array.from(
      container.querySelectorAll<HTMLElement>(".subject-card")
    )
    const groups = Array.from(
      container.querySelectorAll<HTMLElement>(".tier-group")
    )
    subjectCardsRef.current = cards
    tierGroupsRef.current = groups
    subjectMetaRef.current = cards.map((card) => ({
      name: (card.getAttribute("data-subject") || "").toLowerCase(),
      tier: card.closest(".tier-group")?.getAttribute("data-tier") || null
    }))
  }, [])

  const updateVisibility = useCallback(
    (term: string, category: string) => {
      const lower = term.toLowerCase()
      const cards = subjectCardsRef.current
      const meta = subjectMetaRef.current
      if (cards.length === 0) return

      const nextCounts: Record<string, number> = Object.fromEntries(
        categories.map((c) => [c.id, 0])
      ) as Record<string, number>
      let hasVisible = false

      window.requestAnimationFrame(() => {
        const visibleByTier: Record<string, number> = {}

        for (let i = 0; i < cards.length; i++) {
          const m = meta[i]
          const matchesSearch = lower === "" || m.name.includes(lower)
          if (matchesSearch) {
            if (nextCounts["all"] !== undefined) nextCounts["all"] += 1
            if (m.tier && nextCounts[m.tier] !== undefined)
              nextCounts[m.tier] += 1
          }

          const matchesCategory = category === "all" || m.tier === category
          const show = matchesSearch && matchesCategory
          cards[i].classList.toggle("hidden", !show)
          if (show) {
            hasVisible = true
            if (m.tier) visibleByTier[m.tier] = (visibleByTier[m.tier] || 0) + 1
          }
        }

        for (const group of tierGroupsRef.current) {
          const tier = group.getAttribute("data-tier") || ""
          const visibleCount = visibleByTier[tier] || 0
          group.classList.toggle("hidden", visibleCount === 0)
          const countEl = group.querySelector<HTMLElement>(".tier-count")
          if (countEl) countEl.textContent = String(visibleCount)
        }

        setComputedCounts(nextCounts)
        setShowNoResults(!hasVisible)
      })
    },
    [categories]
  )

  useEffect(() => {
    const input = document.getElementById("subject-search") as HTMLInputElement
    if (!input) return
    const timeout = setTimeout(() => {
      updateVisibility(input.value, selectedCategory)
      onSearchChange?.(input.value)
    }, 200)
    return () => clearTimeout(timeout)
  }, [selectedCategory, onSearchChange, updateVisibility])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    onSearchChange?.(value)
    updateVisibility(value, selectedCategory)
  }

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId)
    onCategoryChange?.(categoryId)
    const input = document.getElementById("subject-search") as HTMLInputElement
    if (input) updateVisibility(input.value, categoryId)
  }

  const handleClearSearch = () => {
    setSearchTerm("")
    const input = document.getElementById("subject-search") as HTMLInputElement
    if (input) {
      input.value = ""
      updateVisibility("", selectedCategory)
    }
  }

  return (
    <>
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Search */}
        <div className="flex-1">
          <div className="relative">
            <Search
              aria-hidden
              className="absolute z-10 left-4 top-1/2 transform -translate-y-1/2 text-slate-500 dark:text-slate-400"
              size={20}
            />
            <label className="sr-only" htmlFor="subject-search">
              Search subjects
            </label>
            <input
              autoFocus
              className="w-full pl-12 pr-4 py-3 bg-white/75 dark:bg-slate-900/50 backdrop-blur-sm border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all duration-200"
              id="subject-search"
              onChange={handleInputChange}
              placeholder="Search subjects..."
              type="text"
            />
          </div>
        </div>

        {/* Category Filter */}
        <div className="hidden md:flex gap-2 overflow-x-auto pb-2 lg:pb-0">
          {categories.map((category) => {
            const Icon = categoryIcons[category.name]
            const count = computedCounts[category.id] ?? category.count
            return (
              <TrackedButton
                action="Filter Selected"
                category="page"
                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all duration-200 flex items-center gap-2 ${
                  selectedCategory === category.id
                    ? "bg-emerald-700 text-white"
                    : "bg-white/50 dark:bg-slate-800/50 text-slate-700 dark:text-slate-200 hover:bg-white/80 dark:hover:bg-slate-900/50"
                }`}
                key={category.id}
                label="Subject Category"
                onClick={() => handleCategoryChange(category.id)}
                value={category.name}
              >
                {Icon && <Icon className="w-4 h-4" />}
                {category.name}
                <span
                  className={`inline-flex items-center rounded-full text-[10px] px-1.5 py-0.5 ${
                    selectedCategory === category.id
                      ? "bg-emerald-800 text-white"
                      : "bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300"
                  }`}
                >
                  {count}
                </span>
              </TrackedButton>
            )
          })}
        </div>
      </div>

      {/* No results message */}
      {showNoResults && (
        <div className="text-center py-12">
          <p className="text-slate-500 dark:text-slate-400 mb-4">
            No subjects found for &quot;{searchTerm}&quot;
          </p>
          <TrackedButton
            action="Search Cleared"
            category="page"
            className="text-emerald-500 hover:text-emerald-600 transition-colors"
            label="Clear Search"
            onClick={handleClearSearch}
          >
            Clear search
          </TrackedButton>
        </div>
      )}
    </>
  )
}
